import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAll } from '@/lib/paginate'
import { useHousehold } from '@/context/HouseholdContext'
import type { RecurrenceFrequency } from '@/hooks/useTransactions'
import type { AccountType, TransactionKind, TransactionStatus } from '@/types/db'

export interface RecurringTemplate {
  account_id: string
  category_id: string | ''
  kind: TransactionKind
  amount: number
  currency: string
  description: string | null
  status: TransactionStatus
  tags: string[]
}

export interface RecurringRule {
  id: string
  household_id: string
  account_id: string | null
  frequency: RecurrenceFrequency
  interval: number
  start_date: string | null
  next_date: string
  end_date: string | null
  max_occurrences: number | null
  occurrences_created: number
  /** Semanal em dias escolhidos (0=Dom..6=Sáb). Vazio/null = 1 dia/semana pela âncora. */
  days_of_week: number[] | null
  template: RecurringTemplate
  created_at: string
  account?: { name: string; currency: string; icon: string | null; type: AccountType } | null
}

export function useRecurringRules() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['recurringRules', householdId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_rules')
        .select('*, account:accounts(name, currency, icon, type)')
        .eq('household_id', householdId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as RecurringRule[]
    },
  })
}

// Próxima ocorrência REAL (a partir de hoje) por regra — a menor data das
// ocorrências materializadas com date >= hoje. (O next_date da regra é só o
// cursor de geração, que fica lá na frente após materializar o futuro.)
export function useNextOccurrences() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['nextOccurrences', householdId],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10)
      const rows = await fetchAll<{ recurring_id: string | null; date: string }>((from, to) =>
        supabase
          .from('transactions')
          .select('recurring_id, date')
          .eq('household_id', householdId)
          .not('recurring_id', 'is', null)
          .gte('date', today)
          .order('date', { ascending: true })
          .range(from, to),
      )
      const map = new Map<string, string>()
      for (const r of rows) if (r.recurring_id && !map.has(r.recurring_id)) map.set(r.recurring_id, r.date)
      return map
    },
  })
}

export interface RecurringRuleInput {
  account_id: string
  category_id: string | null
  kind: TransactionKind
  amount: number
  currency: string
  description: string | null
  frequency: RecurrenceFrequency
  interval: number
  maxOccurrences: number | null
  end_date: string | null
  daysOfWeek?: number[] | null // semanal em dias escolhidos (0=Dom..6=Sáb)
}

// Atualiza a regra e REFLETE nas ocorrências ainda PENDENTES (não pagas):
// muda conta, categoria, valor, moeda e descrição. As já pagas (histórico)
// ficam como estão. Depois gera ocorrências que passaram a ser devidas.
export function useUpdateRecurringRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: RecurringRuleInput }) => {
      const template: RecurringTemplate = {
        account_id: values.account_id,
        category_id: values.category_id ?? '',
        kind: values.kind,
        amount: values.amount,
        currency: values.currency,
        description: values.description,
        status: 'paid',
        tags: [],
      }
      const dow =
        values.frequency === 'weekly' && values.daysOfWeek && values.daysOfWeek.length > 0 ? values.daysOfWeek : null
      const { error: ruleErr } = await supabase
        .from('recurring_rules')
        .update({
          account_id: values.account_id,
          frequency: values.frequency,
          interval: values.interval,
          max_occurrences: values.maxOccurrences,
          end_date: values.end_date,
          days_of_week: dow,
          template,
        })
        .eq('id', id)
      if (ruleErr) throw ruleErr

      // reflete nas ocorrências pendentes (mês atual + futuras não pagas)
      const { error: txErr } = await supabase
        .from('transactions')
        .update({
          account_id: values.account_id,
          category_id: values.category_id,
          kind: values.kind,
          amount: values.amount,
          currency: values.currency,
          description: values.description,
        })
        .eq('recurring_id', id)
        .eq('status', 'pending')
      if (txErr) throw txErr
      // (Sem geração automática — as ocorrências são incluídas sob demanda.)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurringRules'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
      void qc.invalidateQueries({ queryKey: ['nextOccurrences'] })
      void qc.invalidateQueries({ queryKey: ['pendingByAccount'] })
      void qc.invalidateQueries({ queryKey: ['pendingUntil'] })
    },
  })
}

// Inclui (materializa) ocorrências recorrentes escolhidas — cria os lançamentos
// linkados por recurring_id. status: pago se a data <= hoje, senão pendente.
export interface RecurringOccurrence {
  ruleId: string
  date: string
  template: RecurringTemplate
}
export function useIncludeRecurring() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: RecurringOccurrence[]) => {
      // NÃO calculamos statement_month aqui, de propósito. A DATA da ocorrência já
      // é a data de VENCIMENTO (as recorrentes são ancoradas no dia do vencimento,
      // como todo o histórico importado), então o mês da data JÁ É o mês da fatura.
      // Rodar suggestStatementMonth em cima disso empurraria de novo — a mesma
      // assinatura cairia em faturas diferentes conforme o mês fosse importado ou
      // incluído aqui. O override de fatura é só para COMPRA manual, onde a data é
      // a da compra real (ver TransactionFormDialog).
      //
      // Entram sempre como PENDENTE — o usuário marca como pago manualmente.
      const rows = items.map((it) => ({
        household_id: householdId,
        account_id: it.template.account_id,
        category_id: it.template.category_id || null,
        kind: it.template.kind,
        amount: it.template.amount,
        currency: it.template.currency,
        date: it.date,
        occurrence_date: it.date,
        description: it.template.description,
        status: 'pending' as const,
        recurring_id: it.ruleId,
        tags: it.template.tags ?? [],
      }))
      const { error } = await supabase.from('transactions').insert(rows)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
      void qc.invalidateQueries({ queryKey: ['nextOccurrences'] })
      void qc.invalidateQueries({ queryKey: ['pendingByAccount'] })
      void qc.invalidateQueries({ queryKey: ['pendingUntil'] })
    },
  })
}

const firstOfMonthISO = (dateISO: string) => `${dateISO.slice(0, 7)}-01`

// Marcadores "N/A nesta OCORRÊNCIA" (recurring_skips): a ocorrência é tratada
// como "não teve" — some de "disponível" e do orçado — SEM criar transação
// nenhuma (antes gerava uma transação de valor 0, que poluía a lista).
// Trava por `occurrence_date` (data exata), NÃO por mês inteiro: uma regra com
// várias ocorrências no mês (ex.: semanal 2x/semana) só pode marcar UMA delas —
// marcar por mês apagava sozinho todas as ocorrências do mês. Bug relatado
// 09/09/2026 (Limpeza Casa, semanal ter/sex, marcar 1 sumia o mês inteiro).
export function useRecurringSkips(year: number, month0: number) {
  const { householdId } = useHousehold()
  const month = `${year}-${String(month0 + 1).padStart(2, '0')}-01`
  return useQuery({
    queryKey: ['recurringSkips', householdId, month],
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from('recurring_skips')
        .select('recurring_id, occurrence_date')
        .eq('household_id', householdId)
        .eq('month', month)
      if (error) throw error
      return new Set((data ?? []).map((r) => `${r.recurring_id}|${r.occurrence_date}`))
    },
  })
}

// TODOS os marcadores N/A do household, indexados por `${recurring_id}|${data}`.
// Usado onde se projeta a cadeia de VÁRIOS meses (Orçamento resultOf, Relatórios).
export function useAllRecurringSkips() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['recurringSkipsAll', householdId],
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from('recurring_skips')
        .select('recurring_id, occurrence_date')
        .eq('household_id', householdId)
      if (error) throw error
      return new Set((data ?? []).map((r) => `${r.recurring_id}|${r.occurrence_date}`))
    },
  })
}

function invalidateSkips(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['recurringSkips'] })
  void qc.invalidateQueries({ queryKey: ['recurringSkipsAll'] })
  void qc.invalidateQueries({ queryKey: ['transactions'] })
}

// Marca ocorrência(s) como N/A (insere marcador; não cria transação). Trava por
// data exata — ver comentário de useRecurringSkips.
export function useSkipRecurring() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: RecurringOccurrence[]) => {
      const rows = items.map((it) => ({
        household_id: householdId,
        recurring_id: it.ruleId,
        month: firstOfMonthISO(it.date),
        occurrence_date: it.date,
      }))
      const { error } = await supabase
        .from('recurring_skips')
        .upsert(rows, { onConflict: 'recurring_id,occurrence_date' })
      if (error) throw error
    },
    onSuccess: () => invalidateSkips(qc),
  })
}

// Desfaz o N/A: remove o marcador → a ocorrência volta a "disponível".
export function useUnskipRecurring() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: { ruleId: string; date: string }[]) => {
      for (const it of items) {
        const { error } = await supabase
          .from('recurring_skips')
          .delete()
          .eq('recurring_id', it.ruleId)
          .eq('occurrence_date', it.date)
        if (error) throw error
      }
    },
    onSuccess: () => invalidateSkips(qc),
  })
}

// Exclui a regra. SEMPRE remove APENAS as ocorrências ainda PENDENTES (não
// pagas/recebidas). As já efetivadas (status 'paid') NUNCA são apagadas — ficam
// no histórico (recurring_id vira null via FK on delete set null).
export function useDeleteRecurring() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // remove só as pendentes vinculadas à regra
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('recurring_id', id)
        .eq('status', 'pending')
      if (error) throw error
      // exclui a regra (as pagas permanecem com recurring_id = null)
      const { error: delErr } = await supabase.from('recurring_rules').delete().eq('id', id)
      if (delErr) throw delErr
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurringRules'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
      void qc.invalidateQueries({ queryKey: ['nextOccurrences'] })
      void qc.invalidateQueries({ queryKey: ['pendingByAccount'] })
      void qc.invalidateQueries({ queryKey: ['pendingUntil'] })
    },
  })
}
